// paths.ts

/**
 * Helper function to construct paths
 * @param root - The root path
 * @param sublink - The sublink to append to the root
 * @returns The combined path
 */
function path(root: string, sublink: string): string {
  return `${root}${sublink}`.replace(/\/\//g, '/');
}

// Root path
const ROOT = '/';

// Authentication-related routes
export const PATH_AUTH = {
  root: ROOT,
  clockingApp: {
    clockingInAndOut: path(ROOT, '/pointages'),
    clockingInit: path(ROOT, '/pointages/init'),
    clockingDeviceType: path(ROOT, '/pointages/device-type'),
    clockingAppDisabled: path(ROOT, '/pointages/app-disabled'),
    devicePairing: path(ROOT, '/pointages/pairing'),
    settings: path(ROOT, '/pointages/settings'),
    forgotClockingCode: path(ROOT, '/pointages/forgot-clocking-code'),
    login: path(ROOT, '/pointages/login'),
    resourceHome: path(ROOT, '/pointages/home'),
    clientHome: path(ROOT, '/pointages/client-home'),
    clientPlanning: path(ROOT, '/pointages/client-planning'),
    managerHome: path(ROOT, '/pointages/manager-home'),
  },
  chooseCompany: path(ROOT, '/choose-company'),
  login: path(ROOT, '/login'),
  resetPassword: path(ROOT, '/reset-password'),
} as const;

// Application pages
export const PATH_PAGE = {
  root: ROOT,
  about: path(ROOT, '/about'),
  maintenance: path(ROOT, '/maintenance'),
  page404: path(ROOT, '/404'),
  page500: path(ROOT, '/500'),

  settings: {
    root: path(ROOT, '/settings'),
    password: path(ROOT, '/settings/password'),
    notifications: path(ROOT, '/settings/notifications'),
  },

  candidate: path(ROOT, '/candidate'),

  clockingApp: {
    clockablesShiftsList: path(ROOT, '/pointages/clockables-shifts-list'),
    clockingAppDisabled: path(ROOT, '/pointages/app-disabled'),
    clockingsShiftsLists: path(ROOT, '/pointages/clockings-shifts-lists'),
    consignesDocuments: path(ROOT, '/pointages/consignes-documents'),
    shift: path(ROOT, '/pointages/shift'),
    sitesManagement: path(ROOT, '/pointages/sites-management'),

    mce: {
      managerEvents: path(ROOT, '/pointages/mce/manager/events'),
      resourceEvents: path(ROOT, '/pointages/mce/resource/events'),
      resourcePatrols: path(ROOT, '/pointages/mce/resource/patrols'),
    },
  },

  general: {
    resource: {
      root: path(ROOT, '/resource'),
      editResource: path(ROOT, '/resource/edit-resource'), // Création / modification d'un agent sous-traitant
      resources: path(ROOT, '/resource/resources'),
      resourceSheet: path(ROOT, '/resource/resource'),
      resourceDocuments: path(ROOT, '/resource/resource/documents'),
    },
    site: {
      root: path(ROOT, '/site'),
      siteList: path(ROOT, '/site/sites'),
      siteSheet: path(ROOT, '/site/site'),
      siteSchedule: path(ROOT, '/site-schedule'),
    },
    demand: path(ROOT, '/demand'),
    services: path(ROOT, '/services'),
  },

  manager: {
    root: path(ROOT, '/manager'),
    clockingDevices: path(ROOT, '/clockings/devices'),
    linkClockingDevice: path(ROOT, '/clockings/link-device'),
    print: path(ROOT, '/manager/print'),

    mce: {
      root: path(ROOT, '/mce/manager/forms'),
      formsCreate: path(ROOT, '/mce/manager/forms/create'),
      formsUpdate: path(ROOT, '/mce/manager/forms/update'),
      entries: path(ROOT, '/mce/manager/entries'),
      entry: path(ROOT, '/mce/manager/entry'),
      events: path(ROOT, '/mce/manager/events'),
      patrols: path(ROOT, '/mce/manager/patrols'),
      patrolsCreate: path(ROOT, '/mce/manager/patrols/create'),
      patrolsUpdate: path(ROOT, '/mce/manager/patrols/update'),
    },
  },

  resource: {
    root: path(ROOT, '/resource'),
    availabilities: path(ROOT, '/availabilities'),
    missions: path(ROOT, '/missions'),
    schedule: path(ROOT, '/schedule'),
    sheet: path(ROOT, '/sheet'),
    demands: path(ROOT, '/demands'),

    mce: {
      root: path(ROOT, '/mce/resource/events'),
      formsFill: path(ROOT, '/mce/resource/events/fill'),
      patrols: path(ROOT, '/mce/resource/patrols'),
    },
  },

  client: {
    root: path(ROOT, '/client'), // Dashboard
    invoices: path(ROOT, '/invoices'), // Factures
    myDocuments: path(ROOT, '/client/documents'), // Documents du client
    providers: path(ROOT, '/providers'), // Mes prestataires
    provider: path(ROOT, '/provider'), // Mon prestataire
    providerDocuments: path(ROOT, '/provider/documents'), // Documents de mon prestataire
    quotes: path(ROOT, '/quotes'), // Devis
  },

  manager_partner: {
    root: path(ROOT, '/partner'), // Dashboard
    myCompany: path(ROOT, '/partner/myCompany'), // Ma société
    myCompanyDocuments: path(ROOT, '/partner/myCompany/documents'), // Documents de la société sous-traitante
    orders: path(ROOT, '/orders'), // Commandes
    order: path(ROOT, '/orders/order'), // Commande
    partners: path(ROOT, '/partner/partners'), // Mes partenaires
    partner: path(ROOT, '/partner/partner'), // Société partenaire
    partnerDocuments: path(ROOT, '/partner/partner/documents'), // Documents d'une société partenaire
    partnerDemands: path(ROOT, '/demands/partner'), // Demandes partenaire
  },

  user: {
    root: path(ROOT, '/user'),
  },

  admin: {
    root: path(ROOT, '/admin'),
    permissions: path(ROOT, '/admin/permissions'),
    users: path(ROOT, '/admin/users'),
    userDetail: (userId: number | string) => path(ROOT, `/admin/users/edit/${userId}`),
  },
} as const;